<template>
  <template v-if="shouldShowRouteButtons">
    <f7-button
      v-if="shouldShowStartRoute"
      large
      title="Iniciar trayecto"
      :disabled="!isAllowedToDoRouteOperation"
      @click="onStartRouteClick"
    >
      <UCSvgIcon
        name="btn-next"
        width="46px"
        height="46px"
      />
    </f7-button>

    <f7-button
      v-else-if="shouldShowPause"
      large
      title="Pausar trayecto"
      :disabled="!isAllowedToDoRouteOperation"
      @click="handlePauseRouteAction"
    >
      <UCSvgIcon
        name="ic_stop"
        width="46px"
        height="46px"
      />
    </f7-button>

    <f7-button
      v-else-if="shouldShowResume"
      large
      title="Reanudar trayecto"
      :disabled="!isAllowedToDoRouteOperation"
      @click="handleResumeRouteAction"
    >
      <UCSvgIcon
        name="ic_play_btn_yellow"
        width="46px"
        height="46px"
      />
    </f7-button>
  </template>

  <f7-button
    v-if="shouldShowStart"
    large
    title="Iniciar el parte"
    @click="onStartPartClick"
  >
    <UCSvgIcon
      name="ic_play_green"
      width="46px"
      height="46px"
    />
  </f7-button>

  <f7-button
    v-if="shouldShowAccess"
    large
    title="Acceder al parte"
    @click="accessPart"
  >
    <img
      src="@/static/img/ic_detail.svg"
      width="46px"
      height="46px"
    >
  </f7-button>

  <!-- Popup: Select work position -->
  <PCSelectWorkPositionPopup
    v-model:opened="isSelectWorkPositionPopupOpened"
    @apply="handleWorkPositionSelection"
  />

  <!-- Popup: Select workers -->
  <PCSelectItemsPopup
    v-model:opened="isSelectWorkersPopupOpened"
    :items="selectWorkersData"
    :min="1"
    title="Seleccionar trabajadores"
    item-title-prop="Sujeto.NombreCompleto"
    @apply="handleWorkersSelection"
  />
</template>

<script setup>
// @ts-check

import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRepairPartsStore } from '@/js/stores/repairParts';
import { useRepairParts } from '@/composables/useRepairParts';
import { useRepairPartsPauseResume } from '@/composables/useRepairPartsPauseResume';
import { useParamRef } from '@erpagroapp/params';

import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { callStartPart, callStartRoute } from '@/js/services/repairs/ApiRepairs';
import { logError } from '@/js/utils/log';
import { SelectionActions } from '@/js/enums/externalRepairParts';
import { moveItemToFrontByProp } from '@/js/utils/moveItemToFrontByProp';

import PCSelectItemsPopup from '@/components/common/PCSelectItemsPopup.vue';
import UCSvgIcon from '@/components/common/UCSvgIcon.vue';
import PCSelectWorkPositionPopup from '../PCSelectWorkPositionPopup.vue';

/** @import { PropType } from 'vue' */
/** @import { WorkPartItem } from '@/js/stores/repairParts' */
/**
 * @import {
 * DatosParteReparacion,
 * PuestoTrabajo,
 * Trabajador,
 * } from '@erpagroapp/types' */

const props = defineProps({
  workPart: {
    /** @type {PropType<WorkPartItem>} */
    type: Object,
    default: null,
  },
});

const { workParts } = useRepairParts();

const {
  isAllowedToDoRouteOperation,
  loadWorkParts,
  loadExpectedWorkers,
  navigateToPartDetails,
} = useRepairParts();

const {
  isSelectWorkersPopupOpened,
  selectWorkersData,
  selectedWorkers,
  selectedAction,
  isSelectWorkPositionPopupOpened,
  selectedWorkPosition,
  registerPauseAction,
  handlePauseActionNextStep,
  registerResumeAction,
  handleResumeActionNextStep,
} = useRepairPartsPauseResume();

const {
  worker,
} = storeToRefs(useRepairPartsStore());

const partStatusCreatedStreet = useParamRef('EstadoDefectoPartesReparacionCalle');
const partStatusCreatedWorkshop = useParamRef('EstadoDefectoPartesReparacion');
const partStatusRouteStarted = useParamRef('EstadoParteReapracionInicioDesplazamiento');
const partStatusStarted = useParamRef('EstadoParteReparacionEnCliente');
const partStatusValued = useParamRef('EstadoValoracionDefectoParteReparacion');

const isPartFinished = computed(() => props.workPart.FechaFinalizacionOrden !== null);

const workPartRouteStarted = computed(() => workParts.value?.find(
  (item) => item.Codigo_ParteProduccionEstado === partStatusRouteStarted.value,
) || null);

const shouldShowRouteButtons = computed(() => !isPartFinished.value
  && !!props.workPart.TrabajosEnDomicilio);

const shouldShowStartRoute = computed(() => shouldShowRouteButtons.value
  && props.workPart.Codigo_ParteProduccionEstado === partStatusCreatedStreet.value
  && (!workPartRouteStarted.value || (workPartRouteStarted.value && workPartRouteStarted.value.NumeroTrabajadoresActivos === 0)));

const shouldShowPause = computed(() => shouldShowRouteButtons.value
  && !shouldShowStartRoute.value
  && !isPartFinished.value
  && !!props.workPart.TieneIntervencionActiva
  && props.workPart.Codigo_ParteProduccionEstado === partStatusRouteStarted.value);

const shouldShowResume = computed(() => shouldShowRouteButtons.value
  && !shouldShowStartRoute.value
  && !shouldShowPause.value
  && props.workPart.Codigo_ParteProduccionEstado === partStatusRouteStarted.value
  && !props.workPart.TieneIntervencionActiva);

const shouldShowStart = computed(() => {
  if (isPartFinished.value) {
    return false;
  }

  if (workPartRouteStarted.value
    && workPartRouteStarted.value !== props.workPart
    && workPartRouteStarted.value.NumeroTrabajadoresActivos > 0
  ) {
    // There is a different work part that has a route started and that is not paused
    return false;
  }

  if (props.workPart.TrabajosEnDomicilio) {
    if (props.workPart.Codigo_ParteProduccionEstado !== partStatusRouteStarted.value) {
      // Work part is started in the 'street' type
      return false;
    }

    if (props.workPart.NumeroTrabajadoresActivos === 0) {
      // Work part is paused
      return false;
    }
  } else if (props.workPart.Codigo_ParteProduccionEstado !== partStatusCreatedWorkshop.value) {
    // Work part is started in the 'workshop' type
    return false;
  }

  return true;
});

const shouldShowAccess = computed(() => {
  if (isPartFinished.value) {
    // Part is finished, can access
    return true;
  }

  const validStatuses = new Set([
    partStatusStarted.value,
    partStatusValued.value,
  ]);

  if (validStatuses.has(props.workPart.Codigo_ParteProduccionEstado)) {
    return true;
  }

  return false;
});

/**
 * Starts the route for a specific repair part and workers.
 * @param {PartialDeep<DatosParteReparacion>} repairPartData - The repair part data.
 */
async function startRoute(repairPartData) {
  HiDialog.Preloader(true, 'Iniciando el camino...');

  try {
    const { data } = await callStartRoute(repairPartData);

    if (data.Valor === true) {
      HiNotification.Toast('El trayecto se ha iniciado correctamente');
      loadWorkParts();
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido iniciar el trayecto');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido iniciar el trayecto');
  } finally {
    HiDialog.Preloader(false);

    isSelectWorkersPopupOpened.value = false;
    selectedWorkers.value = [];
  }
}

/** A method that is used to handle the 'Start Route' functionality */
async function handleStartRouteAction() {
  if (props.workPart.NumeroTrabajadoresPrevistos > 1 && selectedWorkers.value.length === 0) {
    // When there is more than one expected workers,
    // user must select for which workers the route will start

    try {
      selectWorkersData.value = await loadExpectedWorkers(props.workPart.Id_ParteProduccion);
      isSelectWorkersPopupOpened.value = true;

      return;
    } catch {
      HiDialog.Preloader(true, 'No se han podido obtener los trabajadores previstos para este parte');
      return;
    }
  }

  if (selectedWorkers.value.length === 0 && worker.value) {
    // There is < 1 expected workers, so set user as worker
    selectedWorkers.value = [worker.value];
  }

  const dataToSend = {
    ParteProduccion: {
      Id: props.workPart.Id_ParteProduccion,
    },
    Trabajadores: selectedWorkers.value,
  };

  startRoute(dataToSend);
}

function checkIsAllowedToDoRouteOperation() {
  if (!isAllowedToDoRouteOperation.value) {
    HiNotification.Toast('No está permitido realizar operaciones de trayecto de otros días');
    return false;
  }
  return true;
}

/** Handles the 'Start Route' button click. */
async function onStartRouteClick() {
  if (!checkIsAllowedToDoRouteOperation()) {
    return;
  }

  selectedAction.value = SelectionActions.START_ROUTE;
  selectedWorkers.value = [];

  handleStartRouteAction();
}

/** A method that is used to handle the 'Pause Route' functionality */
async function handlePauseRouteAction() {
  if (!props.workPart) {
    return;
  }

  if (!checkIsAllowedToDoRouteOperation()) {
    return;
  }

  const repairPartData = await handlePauseActionNextStep(
    props.workPart.Id_ParteProduccion,
    props.workPart.NumeroTrabajadoresActivos,
  );

  if (!repairPartData) {
    return;
  }

  const successful = await registerPauseAction(repairPartData, {
    preloaderMsg: 'Pausando el trayecto...',
    successMsg: 'El trayecto se ha pausado correctamente',
    errorMsg: 'No se ha podido pausar el trayecto',
  });

  if (successful) {
    loadWorkParts();
  }
}

/** A method that is used to handle the 'Resume Route' functionality */
async function handleResumeRouteAction() {
  if (!props.workPart) {
    return;
  }

  if (!checkIsAllowedToDoRouteOperation()) {
    return;
  }

  const repairPartData = await handleResumeActionNextStep(
    props.workPart.Id_ParteProduccion,
    props.workPart.NumeroTrabajadoresActivos,
    props.workPart.NumeroTrabajadoresPrevistos,
    false,
  );

  if (!repairPartData) {
    return;
  }

  const successful = await registerResumeAction(repairPartData, {
    preloaderMsg: 'Reanudando el trayecto...',
    successMsg: 'El trayecto se ha reanudado correctamente',
    errorMsg: 'No se ha podido reanudar el trayecto',
  });

  if (successful) {
    loadWorkParts();
  }
}

/**
 * Starts the part.
 * @param {PartialDeep<DatosParteReparacion>} repairPartData - The repair part data.
 */
async function startPart(repairPartData) {
  HiDialog.Preloader(true, 'Iniciando el parte...');

  try {
    const { data } = await callStartPart(repairPartData);

    if (data.Valor === true) {
      HiNotification.Toast('El parte se ha iniciado correctamente');

      if (typeof repairPartData.ParteProduccion?.Id === 'number') {
        navigateToPartDetails(repairPartData.ParteProduccion.Id);
      }
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido iniciar el parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido iniciar el parte');
  } finally {
    HiDialog.Preloader(false);

    isSelectWorkPositionPopupOpened.value = false;
    selectedWorkers.value = [];
    selectedWorkPosition.value = null;
  }
}

/** A method that is used to handle the 'Start Part' functionality */
async function handleStartPartAction() {
  if (props.workPart.NumeroTrabajadoresPrevistos > 1 && selectedWorkers.value.length === 0) {
    // When there is more than one expected workers,
    // user must select for which workers the part will start
    try {
      selectWorkersData.value = await loadExpectedWorkers(props.workPart.Id_ParteProduccion);
      isSelectWorkersPopupOpened.value = true;
    } catch (error) {
      logError(error);
      HiDialog.Preloader(true, 'No se han podido obtener los trabajadores previstos para este parte');
    }
    return;
  }

  if (!selectedWorkPosition.value) {
    if (selectedWorkers.value.length > 0) {
      isSelectWorkersPopupOpened.value = false;
    }
    isSelectWorkPositionPopupOpened.value = true;
    return;
  }

  if (selectedWorkers.value.length === 0 && worker.value) {
    // There is 1 expected worker
    selectedWorkers.value = [worker.value];
  }

  /** @type {PartialDeep<DatosParteReparacion>} */
  const repairPartData = {
    ParteProduccion: {
      Id: props.workPart.Id_ParteProduccion,
      ParteProduccionReal: {
        Trabajadores: selectedWorkers.value.map((item) => ({
          Trabajador: {
            Id: item.Id,
          },
          PuestoTrabajo: {
            Id: selectedWorkPosition.value?.Id ?? 0,
          },
        })),
      },
    },
  };

  startPart(repairPartData);
}

/** A method that is called when user clicks on 'Start' to start the part */
function onStartPartClick() {
  selectedAction.value = SelectionActions.START_PART;
  selectedWorkers.value = [];
  selectedWorkPosition.value = null;

  handleStartPartAction();
}

function accessPart() {
  navigateToPartDetails(props.workPart.Id_ParteProduccion);
}

/**
 * Handles the selection of work position in the 'Select the work position' popup.
 * @param {PuestoTrabajo} workPosition - The work position.
 */
function handleWorkPositionSelection(workPosition) {
  selectedWorkPosition.value = workPosition;

  if (selectedAction.value === SelectionActions.START_PART) {
    handleStartPartAction();
  } else if (selectedAction.value === SelectionActions.PAUSE) {
    handlePauseRouteAction();
  }
}

/**
 * Handles the selection of workers in the 'Select the part workers' popup.
 * @param {Trabajador[]} workers - A list of workers.
 */
function handleWorkersSelection(workers) {
  if (!worker.value) {
    return;
  }

  selectedWorkers.value = moveItemToFrontByProp(workers, worker.value.Id, 'Id');

  if (selectedAction.value === SelectionActions.START_ROUTE) {
    handleStartRouteAction();
  } else if (selectedAction.value === SelectionActions.PAUSE) {
    handlePauseRouteAction();
  } else if (selectedAction.value === SelectionActions.RESUME) {
    handleResumeRouteAction();
  } else if (selectedAction.value === SelectionActions.START_PART) {
    handleStartPartAction();
  }
}
</script>
